import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { trackPageView } from '@/lib/googleAnalytics';
import { getOrganizationSchema } from '@/lib/seoSchemas';

/**
 * SEOHelmet component for managing page-specific SEO metadata
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.canonicalUrl - Canonical URL for the page
 * @param {string} props.imageUrl - Image URL for social sharing
 * @param {string} props.pageType - Type of page (website, article, etc.)
 * @param {Array} props.keywords - Keywords for the page
 * @param {Object} props.schema - JSON-LD schema data
 * @param {string} props.path - Current path for analytics tracking
 */
const SEOHelmet = ({
  title = 'CloudAI Leaders | Expert Cloud Computing & AI Training Programs',
  description = 'Join our expert-led Cloud and AI training programs with real-world projects and certifications. Advance your tech career with CloudAI Leaders.',
  canonicalUrl = 'https://www.cloudaileaders.com',
  imageUrl = 'https://www.cloudaileaders.com/images/cloudaiicon.png',
  pageType = 'website',
  keywords = ['cloud computing', 'AI training', 'machine learning', 'cloud certification', 'tech education'],
  schema = null,
  path = null,
}) => {
  const keywordsString = keywords.join(', ');
  const schemaData = schema || getOrganizationSchema();

  useEffect(() => {
    trackPageView(path);
  }, [path]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywordsString} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      <meta property="og:type" content={pageType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="CloudAI Leaders" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;
